import "@/App.css";
import { useEffect } from "react";
import { toast } from "sonner";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Loading } from "@/components/common";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import GlobalDashboard from "@/pages/GlobalDashboard";
import LivDashboard from "@/pages/livraison/LivDashboard";
import ControleForm from "@/pages/livraison/ControleForm";
import LivVehicles from "@/pages/livraison/LivVehicles";
import VehicleFiche from "@/pages/livraison/VehicleFiche";
import Anomalies from "@/pages/livraison/Anomalies";
import ComDashboard from "@/pages/commerciaux/ComDashboard";
import Releve from "@/pages/commerciaux/Releve";
import KmPerso from "@/pages/commerciaux/KmPerso";
import Mooncard from "@/pages/commerciaux/Mooncard";
import Commerciaux from "@/pages/commerciaux/Commerciaux";
import CommercialFiche from "@/pages/commerciaux/CommercialFiche";
import AllVehicles from "@/pages/flotte/AllVehicles";
import UsersPage from "@/pages/settings/UsersPage";
import VehiclesAdmin from "@/pages/settings/VehiclesAdmin";
import Thresholds from "@/pages/settings/Thresholds";
import Backup from "@/pages/settings/Backup";
import Imports from "@/pages/settings/Imports";
import Entretien from "@/pages/Entretien";
import ChangePassword from "@/pages/ChangePassword";
import Profile from "@/pages/Profile";
import MatDashboard, { MatControle, MatList, MatFiche, MatAnomalies, MatDekra, MatDocuments, MatQR } from "@/pages/materiel/Materiel";
import DocumentsPage from "@/pages/DocumentsPage";
import Accidents from "@/pages/livraison/Accidents";
import AccidentForm from "@/pages/livraison/AccidentForm";
import AccidentFiche from "@/pages/livraison/AccidentFiche";
import ComVehicles, { ComVehicleFiche } from "@/pages/commerciaux/ComVehicles";
import { CongesPage } from "@/components/Conges";
import EtatDesLieuxForm from "@/pages/livraison/EtatDesLieuxForm";
import Chauffeurs from "@/pages/livraison/Chauffeurs";
import ChauffeurFiche from "@/pages/livraison/ChauffeurFiche";
import LogiCarburant from "@/pages/livraison/Carburant";
import AdBluePage from "@/pages/livraison/AdBlue";
import Plein from "@/pages/commerciaux/Plein";
import Mobilite from "@/pages/commerciaux/Mobilite";
import MonEspace, { MonSinistre } from "@/pages/commerciaux/MonEspace";
import LogiPlein from "@/pages/livraison/LogiPlein";

const RO_BLOCK = [/^\/livraison\/controle/, /^\/materiel\/controle/, /^\/commerciaux\/releve/, /^\/parametres\/(imports|vehicules|seuils|sauvegarde)/, /^\/livraison\/accidents\/nouveau/, /\/etat-des-lieux$/];

function Denied() {
  useEffect(() => {
    toast.error("Accès non autorisé", { id: "denied" });
  }, []);
  return <Navigate to="/" replace />;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: pathname + window.location.search }} />;
  if (user.must_change_password) return <Navigate to="/changer-mot-de-passe" replace />;
  if (roles && !roles.includes(user.role) && !roles.includes(user.real_role) && !(roles.includes("m2") && user.m2_self)) return <Denied />;
  if (user.real_role === "direction" && RO_BLOCK.some((re) => re.test(pathname))) return <Denied />;
  return children;
}

function HistoryGuard({ children }) {
  const { user } = useAuth();
  const { id } = useParams();
  const self = user.role === "commercial" || (user.m2_self && user.role !== "admin");
  if (self && !user.can_view_history) return <Navigate to="/commerciaux/releve" replace />;
  if (self && id && id !== user.id) return <Denied />;
  return children;
}

const A = ["admin"];
const AC = ["admin", "chauffeur"];
const r = (el, roles) => <Protected roles={roles}>{el}</Protected>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/changer-mot-de-passe" element={<ChangePassword />} />
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/" element={<Home />} />
              <Route path="/profil" element={<Profile />} />
              <Route path="/materiel/dashboard" element={r(<MatDashboard />, ["admin", "chauffeur"])} />
              <Route path="/materiel/controle" element={r(<MatControle />, ["admin", "chauffeur"])} />
              <Route path="/materiel/liste" element={r(<MatList />, ["admin", "chauffeur"])} />
              <Route path="/materiel/fiche/:id" element={r(<MatFiche />, ["admin", "chauffeur"])} />
              <Route path="/materiel/anomalies" element={r(<MatAnomalies />, ["admin", "chauffeur"])} />
              <Route path="/materiel/dekra" element={r(<MatDekra />, ["admin", "chauffeur"])} />
              <Route path="/materiel/documents" element={r(<MatDocuments />, AC)} />
              <Route path="/materiel/qr" element={r(<MatQR />, AC)} />
              <Route path="/documents" element={r(<DocumentsPage />, ["admin", "administratif"])} />
              <Route path="/dashboard" element={r(<GlobalDashboard />, A)} />
              <Route path="/livraison/dashboard" element={r(<LivDashboard />, AC)} />
              <Route path="/livraison/controle" element={r(<ControleForm />, ["admin", "chauffeur"])} />
              <Route path="/livraison/vehicules" element={r(<LivVehicles />, ["admin", "chauffeur"])} />
              <Route path="/livraison/vehicules/:id" element={r(<VehicleFiche />, ["admin", "chauffeur"])} />
              <Route path="/livraison/vehicules/:id/etat-des-lieux" element={r(<EtatDesLieuxForm />, ["admin", "chauffeur"])} />
              <Route path="/livraison/accidents" element={r(<Accidents module="livraison" key="liv" />, ["admin", "chauffeur"])} />
              <Route path="/livraison/accidents/nouveau" element={r(<AccidentForm />, ["admin", "chauffeur"])} />
              <Route path="/livraison/accidents/:id" element={r(<AccidentFiche />, ["admin", "chauffeur"])} />
              <Route path="/livraison/anomalies" element={r(<Anomalies />, AC)} />
              <Route path="/livraison/chauffeurs" element={r(<Chauffeurs />, AC)} />
              <Route path="/livraison/chauffeurs/:id" element={r(<ChauffeurFiche />, AC)} />
              <Route path="/livraison/entretien" element={r(<Entretien vtype="livraison" key="liv" />, ["admin", "chauffeur"])} />
              <Route path="/commerciaux/entretien" element={r(<Entretien vtype="commercial" key="com" />, ["admin", "commercial"])} />
              <Route path="/commerciaux/dashboard" element={r(<ComDashboard />, A)} />
              <Route path="/commerciaux/releve" element={r(<Releve />, ["admin", "commercial", "m2"])} />
              <Route path="/commerciaux/km-perso" element={r(<HistoryGuard><KmPerso /></HistoryGuard>, ["admin", "commercial"])} />
              <Route path="/commerciaux/carburant" element={r(<Mooncard />, A)} />
              <Route path="/commerciaux/mooncard" element={<Navigate to="/commerciaux/carburant" replace />} />
              <Route path="/livraison/carburant" element={r(<LogiCarburant />, AC)} />
              <Route path="/livraison/adblue" element={r(<AdBluePage />, AC)} />
              <Route path="/commerciaux/plein" element={r(<Plein />, ["commercial", "m2"])} />
              <Route path="/commerciaux/mobilite" element={r(<Mobilite />, A)} />
              <Route path="/commerciaux/espace" element={r(<MonEspace />, ["commercial", "m2"])} />
              <Route path="/commerciaux/vehicules" element={r(<ComVehicles />, ["admin", "commercial", "m2"])} />
              <Route path="/commerciaux/vehicules/:id" element={r(<ComVehicleFiche />, ["admin", "commercial", "m2"])} />
              <Route path="/commerciaux/accidents" element={r(<Accidents module="commercial" key="com" />, ["admin", "commercial", "m2"])} />
              <Route path="/commerciaux/conges" element={r(<CongesPage />, A)} />
              <Route path="/commerciaux/accidents/:id" element={r(<AccidentFiche />, A)} />
              <Route path="/commerciaux/sinistres/:id" element={r(<MonSinistre />, ["commercial", "m2"])} />
              <Route path="/livraison/plein" element={r(<LogiPlein />, AC)} />
              <Route path="/commerciaux/equipe" element={r(<Commerciaux />, A)} />
              <Route path="/commerciaux/equipe/:id" element={r(<HistoryGuard><CommercialFiche /></HistoryGuard>, ["admin", "commercial", "m2"])} />
              <Route path="/flotte" element={r(<AllVehicles />, A)} />
              <Route path="/parametres/utilisateurs" element={r(<UsersPage />, A)} />
              <Route path="/parametres/vehicules" element={r(<VehiclesAdmin />, A)} />
              <Route path="/parametres/seuils" element={r(<Thresholds />, A)} />
              <Route path="/parametres/sauvegarde" element={r(<Backup />, A)} />
              <Route path="/parametres/imports" element={r(<Imports />, A)} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
